import { CANCELLATION_PENALTY_TIMINGS, CancellationPenaltyTiming } from '@bandido/shared/types';
import { penaltyDatePreset, applyBookingListPenaltyDateRange } from '../bookingListFilters';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, MenuItem, TextField } from '@mui/material';

import {
  RUTERO_BOOKING_LIST_LIMITS,
  BOOKING_LIST_VERTICALS,
  BookingListVertical,
} from '@bandido/shared/types';

import { HEADER_HEIGHT } from '@frontend/features/common/constants/layout';

import { BookingState } from '../bookingState';
import {
  BOOKING_LIST_STATE_FILTERS,
  BookingListFilters,
  BookingListSortState,
  applyBookingListServiceDateRange,
  applyBookingListVerticalFilter,
  bookingListProviderChoices,
} from '../bookingListFilters';
import { FilterChip, FilterGroup, ResultFiltersBar, SortOption } from './ResultFiltersBar';
import type { BookingListProviderFacet } from '@bandido/shared/types';

/** Lo que se tipea no viaja en cada tecla. */
const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  filters: BookingListFilters;
  onChange: (filters: BookingListFilters) => void;
  /**
   * Solo el texto: llega con retraso (debounce) y para entonces el resto de
   * los filtros pudo haber cambiado. Mandando el objeto entero, lo tipeado
   * hace medio segundo pisaría el chip que se acaba de tocar — la versión de
   * UI del lost update (§21.19).
   */
  onSearchChange: (query?: string) => void;
  onClear: () => void;
  activeCount: number;
  resultCount: number;
  providerFacets?: BookingListProviderFacet[];
  /** Ausente en las pestañas donde el orden ES el trabajo. */
  sort?: {
    value: BookingListSortState;
    options: SortOption[];
    onChange: (value: string) => void;
  };
}

/**
 * Los filtros del listado, con el MISMO contenedor que los buscadores
 * (`ResultFiltersBar`): fijo bajo la barra de navegación en desktop —la lista
 * es larga y se refina mirando filas— y reducido a un botón con el conteo de
 * filtros puestos en mobile, que es lo único usable ahí.
 *
 * No llevan contador por valor: el endpoint publica los contadores de las
 * cuatro PESTAÑAS, no los de cada faceta, y contar sobre la página cargada
 * daría un número que no es el de la agencia. Un contador que miente es peor
 * que ninguno.
 */
export function BookingListFiltersBar({
  filters,
  onChange,
  onSearchChange,
  onClear,
  activeCount,
  resultCount,
  providerFacets,
  sort,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(filters.q ?? '');

  // El texto vuelve a lo que dicen los filtros cuando cambian desde afuera
  // (limpiar, por ejemplo): sin esto el campo mostraría lo que ya no filtra.
  useEffect(() => {
    setQuery(filters.q ?? '');
  }, [filters.q]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed === (filters.q ?? '')) return undefined;
    const timer = setTimeout(
      () => onSearchChange(trimmed || undefined),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
    // El disparador es lo TIPEADO: ponerlos como dependencia reiniciaría el
    // reloj con cada render.
  }, [query]);

  const providers = bookingListProviderChoices(providerFacets, filters);

  return (
    <ResultFiltersBar
      activeCount={activeCount}
      onClear={onClear}
      resultCount={resultCount}
      stickyTop={HEADER_HEIGHT}
      sort={
        sort
          ? {
            value: `${sort.value.sort}:${sort.value.dir}`,
            options: sort.options,
            onChange: sort.onChange,
          }
          : undefined
      }
    >
      {(stacked) => (
        <>
          <TextField select size="small" label={t('penaltyReminders.reconfirmation')}
            value={filters.penaltyReconfirmed === undefined ? '' : String(filters.penaltyReconfirmed)}
            onChange={e => onChange({ ...filters, penaltyReconfirmed: e.target.value === '' ? undefined : e.target.value === 'true' })}
            sx={{ minWidth: 170 }}>
            <MenuItem value="">{t('penaltyReminders.all')}</MenuItem>
            <MenuItem value="true">{t('penaltyReminders.reconfirmed')}</MenuItem>
            <MenuItem value="false">{t('penaltyReminders.notReconfirmed')}</MenuItem>
          </TextField>
          <Button onClick={() => onChange({ ...filters, ...penaltyDatePreset('today') })}>{t('penaltyReminders.today')}</Button>
          <Button onClick={() => onChange({ ...filters, ...penaltyDatePreset('nextWeek') })}>{t('penaltyReminders.nextWeek')}</Button>
          <TextField
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            label={t('travelServices.bookingList.filters.search')}
            placeholder={t('travelServices.bookingList.filters.searchPlaceholder')}
            // El mismo tope que acepta el borde: lo que no entra no se tipea,
            // en vez de recortarse en silencio al enviarlo.
            inputProps={{ maxLength: RUTERO_BOOKING_LIST_LIMITS.queryMaxLength }}
            sx={{ width: stacked ? '100%' : 230 }}
          />
          <TextField
            select
            size="small"
            label={t('rutero.connectors.vertical')}
            value={filters.vertical ?? ''}
            onChange={(event) =>
              onChange(
                applyBookingListVerticalFilter(
                  filters,
                  (event.target.value || undefined) as BookingListVertical | undefined,
                  providerFacets,
                ),
              )
            }
            sx={{ width: stacked ? '100%' : 150 }}
          >
            <MenuItem value="">{t('travelServices.all')}</MenuItem>
            {BOOKING_LIST_VERTICALS.map((vertical) => (
              <MenuItem key={vertical} value={vertical}>
                {t(vertical === 'hotels' ? 'hotelSearch.title' : `rutero.verticals.${vertical}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('rutero.connectors.provider')}
            value={filters.provider ?? ''}
            onChange={(event) =>
              onChange({ ...filters, provider: event.target.value || undefined })
            }
            sx={{ width: stacked ? '100%' : 160 }}
          >
            <MenuItem value="">{t('travelServices.all')}</MenuItem>
            {providers.map((provider) => (
              <MenuItem key={provider} value={provider}>
                {provider}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('rutero.connectors.status')}
            value={filters.state ?? ''}
            onChange={(event) =>
              onChange({
                ...filters,
                state: (event.target.value || undefined) as BookingState | undefined,
              })
            }
            sx={{ width: stacked ? '100%' : 160 }}
          >
            <MenuItem value="">{t('travelServices.all')}</MenuItem>
            {BOOKING_LIST_STATE_FILTERS.map((state) => (
              <MenuItem key={state} value={state}>
                {t(`travelServices.bookingStates.${state}`)}
              </MenuItem>
            ))}
          </TextField>
          <FilterGroup
            label={t('travelServices.bookingList.filters.serviceDate')}
            stacked={stacked}
          >
            <Box display="flex" gap={0.75} flexWrap="wrap">
              <TextField
                size="small"
                type="date"
                value={filters.serviceDateFrom ?? ''}
                onChange={(event) =>
                  onChange(
                    applyBookingListServiceDateRange(
                      filters,
                      'serviceDateFrom',
                      event.target.value || undefined,
                    ),
                  )
                }
                inputProps={{
                  'aria-label': t('travelServices.bookingList.filters.serviceDateFrom'),
                }}
                sx={{ width: 155 }}
              />
              <TextField
                size="small"
                type="date"
                value={filters.serviceDateTo ?? ''}
                onChange={(event) =>
                  onChange(
                    applyBookingListServiceDateRange(
                      filters,
                      'serviceDateTo',
                      event.target.value || undefined,
                    ),
                  )
                }
                inputProps={{
                  'aria-label': t('travelServices.bookingList.filters.serviceDateTo'),
                  min: filters.serviceDateFrom,
                }}
                sx={{ width: 155 }}
              />
            </Box>
          </FilterGroup>
          <FilterGroup label={t('travelServices.bookingList.penalty.label')} stacked={stacked}>
            <TextField select size="small" label={t('travelServices.bookingList.penalty.situation')}
              value={filters.cancellationPenaltyTiming ?? ''}
              onChange={(event) => onChange({ ...filters, cancellationPenaltyTiming: (event.target.value || undefined) as CancellationPenaltyTiming | undefined })}
              sx={{ minWidth: 170, '& input': { fontSize: 'max(1rem, 16px)' } }}>
              <MenuItem value="">{t('travelServices.all')}</MenuItem>
              {CANCELLATION_PENALTY_TIMINGS.map((timing) => <MenuItem key={timing} value={timing}>{t(`travelServices.bookingList.penalty.${timing}`)}</MenuItem>)}
            </TextField>
            {(['cancellationPenaltyFrom', 'cancellationPenaltyTo'] as const).map((part) => (
              <TextField key={part} size="small" type="date" value={filters[part] ?? ''}
                label={t(`travelServices.bookingList.penalty.${part}`)} InputLabelProps={{ shrink: true }}
                onChange={(event) => onChange(applyBookingListPenaltyDateRange(filters, part, event.target.value || undefined))}
                inputProps={{ min: part === 'cancellationPenaltyTo' ? filters.cancellationPenaltyFrom : undefined }}
                sx={{ width: 180, '& input': { fontSize: 'max(1rem, 16px)' } }} />
            ))}
          </FilterGroup>
          {filters.caseId && (
            <FilterChip
              label={t('travelServices.bookingList.filters.case', {
                number: filters.caseId,
              })}
              active
              onClick={() => onChange({ ...filters, caseId: undefined })}
            />
          )}
          <FilterChip
            label={t('travelServices.bookingList.filters.saleOrderless')}
            active={Boolean(filters.saleOrderless)}
            onClick={() =>
              onChange({
                ...filters,
                saleOrderless: filters.saleOrderless ? undefined : true,
              })
            }
          />
        </>
      )}
    </ResultFiltersBar>
  );
}
